import React from 'react';
import { Button, ListGroup, Spinner } from 'react-bootstrap';
import StateLabel from './StateLabel';
import { channelAccountLabel } from '../../../Services/EmailInboxService';
import { conversationAge, exactWhen } from '../../../Utils/conversationAge';

/**
 * THE QUEUE: one row per conversation, newest activity first.
 *
 * THE AGE IS THE LOUDEST THING ON A ROW THAT IS WAITING. That is the design
 * brief in one line — the problem this console exists to make visible is
 * 7–22 hour weekend response times, so on a row nobody has answered or taken
 * the age is large and amber, and everywhere else it is quiet. A list where
 * every row shouts equally measures nothing. "Waiting" is read off the DESK
 * state, not off the archive's: a conversation somebody has taken, or closed
 * as needing no reply, is not waiting on anybody and stops shouting.
 *
 * THE CHANNEL IS ON EVERY ROW, not just in the thread header. There is one
 * channel today and the row still says `email: info@pickuphungary.com`,
 * because more mailboxes and more channels are coming and the moment they
 * arrive the queue is mixed — a reader who has never seen the channel named
 * cannot tell a WhatsApp from a mail at a glance, and by then the habit of
 * not looking is already formed.
 */

/**
 * WHOSE CONVERSATION IT IS, which is not simply the first participant.
 *
 * `participants` is the distinct SENDERS oldest first, so on a thread the
 * office opened — a chase, a follow-up, anything from a template — the first
 * entry is our own mailbox, and the row then reads `info@pickuphungary.com`
 * for every one of them. The first sender that is not the mailbox this
 * conversation arrived ON is the other side of it.
 *
 * IT USES THE CONVERSATION'S OWN `account` AND NOT A DOMAIN RULE. The API
 * decides ours-or-theirs by domain, which also matches the forwarder aliases
 * pointed at this company (mail forwarded through one of them reads as ours),
 * and a second, differently-wrong copy of that judgement in the browser would
 * be worse than this. The cost is that a thread between two of our own
 * mailboxes still labels itself with one of them — which is true, and rare.
 */
export function participantName(conversation) {
  const people = conversation.participants || [];
  const ours = String(conversation.account || '').toLowerCase();
  const theirs = people.find((who) => String(who).toLowerCase() !== ours);
  return theirs || people[0] || conversation.title || 'Unknown sender';
}

function ConversationList({
  conversations, stateOf, selectedId, onSelect, loading, cursor, onLoadMore, loadingMore,
}) {
  if (loading) {
    return (
      <div className="cs-list__empty">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="cs-list__empty text-muted">
        <i className="fa fa-inbox d-block mb-2" />
        Nothing in this view
      </div>
    );
  }

  return (
    <>
      <ListGroup variant="flush" className="cs-list">
        {conversations.map((conversation) => {
          const state = stateOf(conversation);
          const waiting = state.value === 'needs_reply' || state.value === 'draft_ready';
          const when = conversation.last_message?.sent_at;
          return (
            <ListGroup.Item
              action
              key={conversation.id}
              active={conversation.id === selectedId}
              onClick={() => onSelect(conversation.id)}
              className="cs-list__row"
              data-testid="cs-conversation-row"
            >
              <div className="cs-list__who">
                <div className="cs-list__name">{participantName(conversation)}</div>
                <div className="cs-list__title">{conversation.title || 'No subject'}</div>
                <div className="cs-list__channel">{channelAccountLabel(conversation)}</div>
              </div>
              <div className="cs-list__when">
                <div
                  className={`cs-list__age ${waiting ? 'cs-list__age--waiting' : ''}`}
                  title={exactWhen(when)}
                >
                  {conversationAge(when)}
                </div>
                {/* The row is 300px and a reason is a sentence, so the detail
                    is kept for the pane this row opens — which always shows
                    it. See StateLabel. */}
                <StateLabel descriptor={state} showDetail={false} />
              </div>
            </ListGroup.Item>
          );
        })}
      </ListGroup>

      {cursor && (
        <div className="text-center py-2">
          <Button variant="link" size="sm" disabled={loadingMore} onClick={onLoadMore}>
            {loadingMore ? 'Loading…' : 'Load older'}
          </Button>
        </div>
      )}
    </>
  );
}

export default ConversationList;
